import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  User, BookOpen, Users, Settings, 
  ChevronRight, ChevronLeft, X,
  Sparkles, CheckCircle, FileText, Search,
  Plus, Edit, Trash2, Lock
} from 'lucide-react';
import { useSettings } from '../hooks/useSettings';

const Onboarding = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { getPatientLabelPlural, getPatientLabelCapitalized } = useSettings();

  const steps = [
    {
      title: "Bienvenue sur ConfiData Pro ! 🎉",
      description: "Votre solution professionnelle de gestion sécurisée",
      icon: Sparkles,
      color: "from-purple-500 to-pink-500",
      content: (
        <div className="space-y-6 text-center">
          <div className="mx-auto w-32 h-32 rounded-full flex items-center justify-center"
               style={{
                 background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                 boxShadow: '0 20px 60px rgba(102, 126, 234, 0.4)',
               }}>
            <Sparkles className="h-16 w-16 text-white" />
          </div>
          <div className="space-y-4">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              Prêt à découvrir votre nouvel espace ?
            </h3>
            <p className="text-gray-600 text-lg">
              Nous allons vous guider à travers les principales fonctionnalités de ConfiData Pro.
            </p>
            <div className="flex flex-wrap gap-3 justify-center mt-8">
              <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full">
                <Lock className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-900">Sécurisé</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <Sparkles className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Intuitif</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-900">Complet</span>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: `Gérez vos ${getPatientLabelPlural()}`,
      description: "Organisez et suivez toutes vos informations en un seul endroit",
      icon: User,
      color: "from-blue-500 to-cyan-500",
      content: (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Plus className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Créer</h4>
                    <p className="text-gray-600 text-sm">
                      Ajoutez facilement de nouveaux {getPatientLabelPlural()} avec toutes leurs informations.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100 hover:border-green-300 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Search className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Rechercher</h4>
                    <p className="text-gray-600 text-sm">
                      Retrouvez instantanément n'importe quel {getPatientLabelCapitalized()} avec la barre de recherche.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-purple-100 hover:border-purple-300 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Edit className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Modifier</h4>
                    <p className="text-gray-600 text-sm">
                      Mettez à jour les informations à tout moment en toute simplicité.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-orange-100 hover:border-orange-300 transition-all">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <FileText className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-lg mb-2">Notes</h4>
                    <p className="text-gray-600 text-sm">
                      Consultez et ajoutez des notes directement depuis chaque fiche.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-blue-900 mb-1">Astuce Pro</p>
                <p className="text-sm text-blue-700">
                  Utilisez les filtres par groupe pour organiser vos {getPatientLabelPlural()} et accéder rapidement à ce dont vous avez besoin !
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Rédigez vos Notes",
      description: "Gardez une trace de tous vos comptes rendus et observations",
      icon: BookOpen,
      color: "from-amber-500 to-orange-500",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4"
                 style={{
                   background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                   boxShadow: '0 10px 40px rgba(245, 158, 11, 0.3)',
                 }}>
              <BookOpen className="h-12 w-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Éditeur de texte enrichi
            </h3>
            <p className="text-gray-600">
              Créez des notes détaillées avec mise en forme complète
            </p>
          </div>

          <div className="grid gap-4">
            <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold">
                1
              </div>
              <div>
                <h4 className="font-semibold mb-1">Notes globales</h4>
                <p className="text-sm text-gray-600">
                  Créez des notes générales accessibles depuis la section "Mes notes"
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                2
              </div>
              <div>
                <h4 className="font-semibold mb-1">Notes par {getPatientLabelCapitalized()}</h4>
                <p className="text-sm text-gray-600">
                  Attachez des notes spécifiques à chaque {getPatientLabelCapitalized()} pour un suivi personnalisé
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-500 text-white flex items-center justify-center font-bold">
                3
              </div>
              <div>
                <h4 className="font-semibold mb-1">Recherche et filtres</h4>
                <p className="text-sm text-gray-600">
                  Retrouvez instantanément n'importe quelle note grâce à la recherche
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border-2 border-amber-200">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-amber-900 mb-1">Sécurité maximale</p>
                <p className="text-sm text-amber-700">
                  Toutes vos notes sont chiffrées de bout en bout pour garantir la confidentialité.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Organisez en Groupes",
      description: "Catégorisez et gérez vos contacts par groupes",
      icon: Users,
      color: "from-green-500 to-emerald-500",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4"
                 style={{
                   background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                   boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
                 }}>
              <Users className="h-12 w-12 text-white" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-6 bg-green-50 rounded-xl border-2 border-green-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-500 rounded-lg">
                  <Plus className="h-5 w-5 text-white" />
                </div>
                <h4 className="font-semibold text-lg">Créer des groupes</h4>
              </div>
              <p className="text-sm text-gray-600">
                Créez des groupes personnalisés avec des couleurs distinctives pour une organisation visuelle.
              </p>
            </div>

            <div className="p-6 bg-emerald-50 rounded-xl border-2 border-emerald-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-500 rounded-lg">
                  <User className="h-5 w-5 text-white" />
                </div>
                <h4 className="font-semibold text-lg">Affecter des membres</h4>
              </div>
              <p className="text-sm text-gray-600">
                Ajoutez vos {getPatientLabelPlural()} à un ou plusieurs groupes selon vos besoins.
              </p>
            </div>

            <div className="p-6 bg-teal-50 rounded-xl border-2 border-teal-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-teal-500 rounded-lg">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                <h4 className="font-semibold text-lg">Notes de groupe</h4>
              </div>
              <p className="text-sm text-gray-600">
                Rédigez des notes communes pour un groupe entier, parfait pour les séances collectives.
              </p>
            </div>

            <div className="p-6 bg-cyan-50 rounded-xl border-2 border-cyan-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-cyan-500 rounded-lg">
                  <Search className="h-5 w-5 text-white" />
                </div>
                <h4 className="font-semibold text-lg">Filtrage rapide</h4>
              </div>
              <p className="text-sm text-gray-600">
                Filtrez facilement vos {getPatientLabelPlural()} par groupe depuis la page principale.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border-2 border-green-200">
            <div className="flex items-start gap-3">
              <Sparkles className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-medium text-green-900 mb-1">Exemple d'utilisation</p>
                <p className="text-sm text-green-700">
                  Créez des groupes comme "Niveau débutant", "Avancés", "Session du mardi", etc. pour une organisation optimale !
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Personnalisez vos Réglages",
      description: "Adaptez l'application à vos besoins spécifiques",
      icon: Settings,
      color: "from-indigo-500 to-purple-500",
      content: (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-2xl mb-4"
                 style={{
                   background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                   boxShadow: '0 10px 40px rgba(99, 102, 241, 0.3)',
                 }}>
              <Settings className="h-12 w-12 text-white" />
            </div>
          </div>

          <div className="space-y-4">
            <div className="p-5 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-indigo-500 rounded-lg flex-shrink-0">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Type de contact personnalisé</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Choisissez le terme qui vous convient : élève, client, patient, employé, etc.
                  </p>
                  <p className="text-xs text-indigo-600 font-medium">
                    ✨ Le terme s'applique automatiquement dans toute l'application !
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-purple-500 rounded-lg flex-shrink-0">
                  <Edit className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Champs personnalisables</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Créez vos propres champs de données selon vos besoins professionnels.
                  </p>
                  <p className="text-xs text-purple-600 font-medium">
                    📝 Types disponibles : Texte, Nombre, Date, Liste déroulante, Cases à cocher
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl border-2 border-pink-200">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-pink-500 rounded-lg flex-shrink-0">
                  <Lock className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Sécurité et confidentialité</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Gérez votre mot de passe et votre clé de récupération pour un accès sécurisé.
                  </p>
                  <p className="text-xs text-pink-600 font-medium">
                    🔐 Vos données sont chiffrées avec une sécurité AES-256
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Vous êtes prêt ! 🚀",
      description: "Commencez à utiliser ConfiData Pro dès maintenant",
      icon: CheckCircle,
      color: "from-green-500 to-teal-500",
      content: (
        <div className="space-y-6 text-center">
          <div className="mx-auto w-32 h-32 rounded-full flex items-center justify-center"
               style={{
                 background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
                 boxShadow: '0 20px 60px rgba(16, 185, 129, 0.4)',
               }}>
            <CheckCircle className="h-16 w-16 text-white" />
          </div>
          <div className="space-y-4">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-teal-600 bg-clip-text text-transparent">
              Félicitations !
            </h3>
            <p className="text-gray-600 text-lg">
              Vous connaissez maintenant toutes les fonctionnalités principales de ConfiData Pro.
            </p>
            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-xl border-2 border-green-200 max-w-md mx-auto">
              <p className="text-sm text-gray-700 mb-4">
                <strong>Besoin d'aide ?</strong> Vous pouvez toujours accéder aux réglages pour personnaliser votre expérience.
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-green-700">
                <Sparkles className="h-4 w-4" />
                <span>Bonne utilisation de ConfiData Pro !</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const StepIcon = currentStepData.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{
           background: 'rgba(0, 0, 0, 0.7)',
           backdropFilter: 'blur(8px)',
         }}>
      <div className="relative w-full max-w-4xl" style={{ maxHeight: 'calc(100vh - 4rem)' }}>
        <Card className="border-0 shadow-2xl overflow-hidden flex flex-col h-full">
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${currentStepData.color} p-6 text-white relative overflow-hidden flex-shrink-0`}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl"></div>
            </div>
            
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <StepIcon className="h-8 w-8" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold">{currentStepData.title}</h2>
                    <p className="text-white/90 mt-1">{currentStepData.description}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSkip}
                  className="text-white hover:bg-white/20"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-white/20 rounded-full h-2 backdrop-blur-sm">
                <div 
                  className="bg-white h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-sm text-white/80">
                <span>Étape {currentStep + 1} sur {steps.length}</span>
                <span>{Math.round(progress)}%</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <CardContent className="p-6 overflow-y-auto flex-1" style={{ minHeight: 0 }}>
            <div className="animate-in fade-in duration-500">
              {currentStepData.content}
            </div>
          </CardContent>

          {/* Footer with navigation */}
          <div className="border-t bg-gray-50 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className="gap-2"
              >
                <ChevronLeft className="h-4 w-4" />
                Précédent
              </Button>

              <div className="flex gap-2">
                {steps.map((step, index) => (
                  <div
                    key={`step-indicator-${step.title || index}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentStep 
                        ? 'w-8 bg-gradient-to-r from-purple-600 to-pink-600' 
                        : index < currentStep
                        ? 'w-2 bg-green-500'
                        : 'w-2 bg-gray-300'
                    }`}
                  />
                ))}
              </div>

              <Button
                onClick={handleNext}
                className="gap-2"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    Commencer
                    <CheckCircle className="h-4 w-4" />
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>

            {currentStep < steps.length - 1 && (
              <div className="text-center mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSkip}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Passer l'introduction
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Onboarding;
